import React from 'react';
import {
  useHistory
} from "react-router-dom";
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import IconButton from '@material-ui/core/IconButton';
import { Close, LocalMallOutlined } from '@material-ui/icons';
import Snackbar from '@material-ui/core/Snackbar';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';

import Config from './config';
import Logo from './assets/logo.png';
import { checkMobileNumber, formatMobileNumber } from './utils';
import { Divider } from '@material-ui/core';

interface StoreSummary {
  id: string;
  logoImgUrl?: string;
  name: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  header: {
    textAlign: 'center',
  },
  logo: {
    marginBottom: theme.spacing(2),
    height: 80,
    marginTop: theme.spacing(6),
  },
  language: {
    position: 'absolute',
    right: 3,
    top: 3,
  },
  form: {
    '& > *': {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    textAlign: 'center',
    minWidth: 280,
    maxWidth: 340,
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(4),

  },
  formControl: {
    width: 300,
    backgroundColor: '#FFF'
  },
  button: {
    width: 300,
    height: 56,
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    // color: 'white',
    fontSize: 18
  },
  orderButton: {
    width: 200,
    height: 40,
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
}));

const Login = () => {
  const classes = useStyles();
  const history = useHistory();
  const [mobile, setMobile] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [stores, setStores] = React.useState<StoreSummary[]>([]);
  const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (localStorage.getItem('accessToken') && localStorage.getItem('storeId')) history.push('/admin/regisiterrecords');
  });

  const handleSelectStore = (storeId: string) => {
    setOpen(false);
    localStorage.setItem('storeId', storeId);
    history.push('/admin/regisiterrecords');
  };

  const handleSubmit = () => {
    if (!mobile || !password) {
      setErrorMessage('Please enter all required information');
      setSnackbarOpen(true);
      return;
    }

    if (!checkMobileNumber(mobile.trim())) {
      setErrorMessage('Please enter a valid mobile number');
      setSnackbarOpen(true);
      return;
    }
    login();

  };

  const handleClose = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const login = async () => {
    setStores([]);
    try {
      const loginResponse = await fetch(`${Config.apiUrl}account/login/username`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: formatMobileNumber(mobile.trim()),
            password: password.trim(),
            principle: 'MOBILE',
          })
        }
      );
      if (!loginResponse.ok) {
        const message = `An error has occured: ${loginResponse.status}`;
        throw new Error(message);
      }
      const loginResData = await loginResponse.json();
      const { accessToken } = loginResData;
      localStorage.setItem('accessToken', accessToken);

      const storesResponse = await fetch(`${Config.apiUrl}store/profile/admin`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: localStorage.getItem('accessToken') || '',
          },
        }
      );
      if (!storesResponse.ok) {
        const message = `An error has occured: ${storesResponse.status}`;
        throw new Error(message);
      }
      const storesResData = await storesResponse.json();
      const { records } = storesResData;

      if (records.length > 1) {
        setStores(records);
        setOpen(true);
      } else if (records.length === 1) {
        localStorage.setItem('storeId', records[0].id);
        history.push('/admin/regisiterrecords');
      } else {
        setErrorMessage('No Stores Available');
        setSnackbarOpen(true);
      }

    } catch (error) {
      console.error(error.message)
      setErrorMessage(error.message);
      setSnackbarOpen(true);
    }
  };


  return (
    <div className={classes.root}>
      <Container className={classes.header}>
        <img alt="guestbook.linkpos.com.au" src={Logo} className={classes.logo} />

        <Typography variant="h2" component="h2" style={{ fontSize: 30, marginBottom: 30 }}>
          Login
          </Typography>
        <Divider />
      </Container>

      <Container className={classes.form}>
        <TextField
          color="secondary"
          type="number"
          fullWidth
          className={classes.formControl}
          autoComplete="tel"
          required id="mobile"
          label="Mobile"
          variant="outlined"
          value={mobile}
          onChange={e => setMobile(e.target.value)}
        />
        <TextField
          color="secondary"
          fullWidth
          className={classes.formControl}
          type="password"
          // autoComplete="new-password"
          required id="password"
          label="Password"
          variant="outlined"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLTextAreaElement).blur();
              handleSubmit();
            }
          }
          }
        />
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disableElevation
          size="large"
          className={classes.button}
        >
          Login
        </Button>
        <Button
          onClick={() => history.push('/password')}
        >
          Forgot Password?
        </Button>
        {/* <Typography variant="caption" component="p" >
          © {moment().format('YYYY')} Link Tech, Inc. All rights reserved.
        </Typography> */}
      </Container>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleClose}
        message={errorMessage}
        action={
          <React.Fragment>
            <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)}>
              <Close fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      />
      <SimpleDialog open={open} onSelectStore={handleSelectStore} stores={stores} />
    </div>
  );
};

interface SimpleDialogProps {
  open: boolean;
  onSelectStore: (value: string) => void;
  stores: StoreSummary[];
}

function SimpleDialog(props: SimpleDialogProps) {
  const { onSelectStore, open, stores } = props;
  const handleSelectStore = (storeId: string) => {
    onSelectStore(storeId);
  };
  return (
    <Dialog aria-labelledby="simple-dialog-title" open={open}>
      <DialogTitle id="simple-dialog-title">Select Store</DialogTitle>
      <List>
        {stores.map((store) => (
          <ListItem button onClick={() => handleSelectStore(store.id)} key={store.id}>

            <ListItemAvatar>
              <Avatar>
                <LocalMallOutlined />
              </Avatar>
            </ListItemAvatar>
            <ListItemText primary={store.name} />
          </ListItem>
        ))}
      </List>
    </Dialog>
  );
}

export default Login;